/* ONE TUNNE <collection-filters>: filters apply as soon as a value is picked - no Apply button.
 *
 * Checkboxes and the sort select apply on change, the price fields after a short pause in
 * typing. The new result is fetched through the Section Rendering API (?section_id=) and swapped
 * in place, the URL is updated with history.replaceState, so the page does not reload and the
 * open filter panel, the scroll position and the focus stay where they were. Without JavaScript
 * the form keeps its Apply button and submits normally.
 *
 * An open filter panel closes on a pointer press outside it and on Escape (focus returns to
 * its summary); opening one panel closes the others. The result of a request that lands while
 * a slider thumb is still held is not swapped in: replacing the DOM under the pointer would end
 * the drag and land the release outside the panel. The release re-applies instead.
 *
 * <price-range> turns the From/To fields into a two-thumb slider bounded by 0 and the highest
 * price of the collection (data-max). Thumbs and fields are kept in sync both ways; the min thumb
 * cannot pass the max thumb. The fields keep the name attributes, so the sliders only change
 * what the form already submits.
 */
(() => {
  if (!customElements.get('price-range')) {
    class PriceRange extends HTMLElement {
      connectedCallback() {
        this.max = Number(this.dataset.max) || 1;
        this.thumbs = {
          min: this.querySelector('[data-range-thumb="min"]'),
          max: this.querySelector('[data-range-thumb="max"]'),
        };
        this.fields = {
          min: this.querySelector('[data-range-field="min"]'),
          max: this.querySelector('[data-range-field="max"]'),
        };
        this.fill = this.querySelector('[data-range-fill]');
        if (!this.thumbs.min || !this.thumbs.max || !this.fields.min || !this.fields.max) return;
        this.addEventListener('input', (event) => this.onInput(event));
        this.paint();
      }

      value(kind) {
        const raw = this.fields[kind].value;
        if (raw === '') return kind === 'min' ? 0 : this.max;
        const num = Number(raw);
        if (Number.isNaN(num)) return kind === 'min' ? 0 : this.max;
        return Math.min(Math.max(num, 0), this.max);
      }

      onInput(event) {
        const thumb = event.target.dataset.rangeThumb;
        if (thumb) {
          this.fromThumb(thumb);
          return;
        }
        if (event.target.dataset.rangeField) this.fromField();
      }

      /* Dragging a thumb: write the value to its field, keep min <= max, and leave the field
         empty when the thumb sits on the bound (an empty field means "no limit" in the URL). */
      fromThumb(kind) {
        let min = Number(this.thumbs.min.value);
        let max = Number(this.thumbs.max.value);
        if (min > max) {
          if (kind === 'min') max = min;
          else min = max;
          this.thumbs.min.value = min;
          this.thumbs.max.value = max;
        }
        this.fields.min.value = min > 0 ? String(min) : '';
        this.fields.max.value = max < this.max ? String(max) : '';
        this.paint();
      }

      fromField() {
        const min = this.value('min');
        const max = this.value('max');
        this.thumbs.min.value = Math.min(min, max);
        this.thumbs.max.value = Math.max(min, max);
        this.paint();
      }

      paint() {
        const min = Number(this.thumbs.min.value);
        const max = Number(this.thumbs.max.value);
        if (this.fill) {
          this.fill.style.setProperty('--fill-start', (min / this.max) * 100 + '%');
          this.fill.style.setProperty('--fill-end', 100 - (max / this.max) * 100 + '%');
        }
        /* The thumb on top must be the one that can still move: when both sit on the max bound
           the min thumb has to win, otherwise the max thumb covers it and the pair is stuck. */
        this.thumbs.min.style.zIndex = min >= this.max ? '2' : '1';
      }
    }

    customElements.define('price-range', PriceRange);
  }

  if (customElements.get('collection-filters')) return;

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.sectionId = this.dataset.sectionId;
      this.classList.add('is-live');
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDocumentPointerUp = this.onDocumentPointerUp.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-filter-link]');
        if (!link) return;
        event.preventDefault();
        this.load(link.getAttribute('href'));
      });
      this.addEventListener('toggle', (event) => this.onToggle(event), true);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('pointerup', this.onDocumentPointerUp);
      document.addEventListener('pointercancel', this.onDocumentPointerUp);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('pointerup', this.onDocumentPointerUp);
      document.removeEventListener('pointercancel', this.onDocumentPointerUp);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* One panel at a time: opening a filter closes the others. */
    onToggle(event) {
      const opened = event.target;
      if (!opened.matches || !opened.matches('details[data-filter-key]') || !opened.open) return;
      this.querySelectorAll('details[data-filter-key][open]').forEach((panel) => {
        if (panel !== opened) panel.open = false;
      });
    }

    /* A press on another filter's summary is left to the toggle handler: closing the open panel
       first would shift the layout on mobile (panels are in flow there) and the tap would miss. */
    onDocumentPointerDown(event) {
      if (!event.target.closest) return;
      if (event.target.closest('[data-range-thumb]')) this.dragging = true;
      const inFilter = event.target.closest('details[data-filter-key]');
      if (inFilter && this.contains(inFilter)) return;
      this.querySelectorAll('details[data-filter-key][open]').forEach((panel) => {
        if (!panel.contains(event.target)) panel.open = false;
      });
    }

    onDocumentPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.pending) {
        this.pending = false;
        this.apply();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const panel = this.querySelector('details[data-filter-key][open]');
      if (!panel) return;
      panel.open = false;
      const summary = panel.querySelector('summary');
      if (summary) summary.focus();
    }

    isPriceInput(target) {
      return target.type === 'number' || target.type === 'range';
    }

    /* Price: a thumb applies on release (change), with a short pause so held arrow keys
       collapse into one request; a typed field applies after a pause in typing. */
    onChange(event) {
      if (this.isPriceInput(event.target)) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.apply(), 250);
        return;
      }
      this.apply();
    }

    onInput(event) {
      if (event.target.type !== 'number') return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.apply(), 700);
    }

    apply() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const query = params.toString();
      this.load(this.form.getAttribute('action') + (query ? '?' + query : ''));
    }

    async load(url) {
      const section = document.getElementById('shopify-section-' + this.sectionId);
      if (!section) {
        window.location.href = url;
        return;
      }
      /* A thumb release fires both pointerup and change: the same URL is loaded once. */
      if (url === this.loadedUrl) return;
      const openFilter = this.querySelector('details[open]');
      const openKey = openFilter && openFilter.dataset.filterKey;
      const active = document.activeElement;
      const focusId = active && active.id;
      const focusThumb = active && active.dataset ? active.dataset.rangeThumb : null;
      const request = (this.request = (this.request || 0) + 1);
      section.setAttribute('aria-busy', 'true');
      try {
        const sep = url.includes('?') ? '&' : '?';
        const response = await fetch(url + sep + 'section_id=' + this.sectionId);
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        if (request !== this.request) return;
        if (this.dragging) {
          this.pending = true;
          return;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById('shopify-section-' + this.sectionId);
        section.innerHTML = fresh ? fresh.innerHTML : doc.body.innerHTML;
        window.history.replaceState({}, '', url);
        this.loadedUrl = url;
        if (openKey) {
          const again = section.querySelector('details[data-filter-key="' + openKey + '"]');
          if (again) again.open = true;
        }
        const el = focusId
          ? document.getElementById(focusId)
          : focusThumb && section.querySelector('[data-range-thumb="' + focusThumb + '"]');
        if (el) el.focus({ preventScroll: true });
      } catch (error) {
        window.location.href = url;
      } finally {
        section.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('collection-filters', CollectionFilters);
})();
